import { useState, useCallback } from 'react';
import { runTransaction, doc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { useProducts } from './useProducts';
import { useStockMovements } from './useStockMovements';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useSecurityVerification } from '../../../hooks/useSecurityVerification';
import { useSimplifiedSecurity } from '../../../hooks/useSimplifiedSecurity';

export const useInventory = () => {
  const { organizationId } = useOrganization();
  
  // Initialize security verification (keeping for backward compatibility)
  const {
    isMembershipVerified,
    verificationLoading: securityLoading,
    verificationError: securityError
  } = useSecurityVerification(organizationId);
  
  // Initialize simplified security (new approach)
  const {
    canManageInventory,
    canViewInventory,
    validateAccess,
    isAuthenticated
  } = useSimplifiedSecurity();
  
  const productsHook = useProducts();
  const stockMovementsHook = useStockMovements();
  const [isProcessing, setIsProcessing] = useState(false);

  // Combined loading state
  const loading = productsHook.loading || stockMovementsHook.loading;
  
  // Combined error state
  const error = productsHook.error || stockMovementsHook.error;

  // Stock In operation with transaction safety
  const performStockIn = useCallback(async (stockInData) => {
    // ✅ SIMPLIFIED SECURITY: Single check for user experience
    if (!isAuthenticated) {
      throw new Error('Please log in and select an organization');
    }

    // Check if user can manage inventory (UX check - Firebase rules handle real security)
    const validation = validateAccess('CAN_MANAGE_INVENTORY');
    if (!validation.hasAccess) {
      throw new Error(validation.errorMessage);
    }

    setIsProcessing(true);
    try {
      const productRef = doc(db, `organizations/${organizationId}/products/${stockInData.productId}`);
      const movementsRef = collection(db, `organizations/${organizationId}/stock-movements`);
      const movementDocRef = doc(movementsRef);
      
      // FIX: Use runTransaction to read current stock atomically and prevent race conditions
      const result = await runTransaction(db, async (transaction) => {
        // 1. Read current product stock inside transaction
        const productDoc = await transaction.get(productRef);
        
        if (!productDoc.exists()) {
          throw new Error('Product not found');
        }
        
        const productData = productDoc.data();
        let currentStock;
      
      if (stockInData.variantId) {
          // Complex product with variant - get current stock from variant
          const variant = productData.variants?.find(v => v.id === stockInData.variantId);
          if (!variant) {
            throw new Error('Variant not found');
          }
          currentStock = variant.currentStock || 0;
        } else {
          // Simple product - get current stock directly
          currentStock = productData.currentStock || 0;
        }
        
        // 2. Calculate new stock based on transaction-read value
        const newStock = currentStock + stockInData.quantity;
        
        // 3. Update product stock
        if (stockInData.variantId) {
          const updatedVariants = productData.variants.map(variant => 
          variant.id === stockInData.variantId 
            ? { ...variant, currentStock: newStock }
            : variant
        );
        
          transaction.update(productRef, {
          variants: updatedVariants,
          updatedAt: serverTimestamp(),
          lastStockUpdate: serverTimestamp(),
          lastStockOperation: 'stock_in',
          lastModifiedBy: stockInData.createdBy
        });
      } else {
          transaction.update(productRef, {
          currentStock: newStock,
          updatedAt: serverTimestamp(),
          lastStockUpdate: serverTimestamp(),
          lastStockOperation: 'stock_in',
          lastModifiedBy: stockInData.createdBy
        });
      }

        // 4. Add stock movement record
      const movementData = {
        productId: stockInData.productId,
        productName: stockInData.productName,
        variantId: stockInData.variantId || null,
        variantName: stockInData.variantName || null,
        challanNumber: null, // Stock In doesn't have challan/party
        partyName: null,
        quantity: stockInData.quantity,
          previousStock: currentStock, // Use transaction-read value
        newStock: newStock,
        reason: stockInData.reason,
        notes: stockInData.notes,
        type: 'stock_in',
        timestamp: serverTimestamp(),
        lastStockUpdate: serverTimestamp(),
        createdBy: stockInData.createdBy,
        operation: 'add'
      };
      
        transaction.set(movementDocRef, movementData);

      return { success: true, newStock, movementId: movementDocRef.id };
      });

      return result;
    } catch (error) {
      throw new Error(`Failed to perform stock in: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  }, [canManageInventory, organizationId, productsHook.products, validateAccess, isAuthenticated]);

  // Stock Out operation with transaction safety
  const performStockOut = useCallback(async (stockOutData) => {
    // ✅ SIMPLIFIED SECURITY: Single check for user experience
    if (!isAuthenticated) {
      throw new Error('Please log in and select an organization');
    }

    // Check if user can manage inventory (UX check - Firebase rules handle real security)
    const validation = validateAccess('CAN_MANAGE_INVENTORY');
    if (!validation.hasAccess) {
      throw new Error(validation.errorMessage);
    }

    setIsProcessing(true);
    try {
      const productRef = doc(db, `organizations/${organizationId}/products/${stockOutData.productId}`);
      const movementsRef = collection(db, `organizations/${organizationId}/stock-movements`);
      const movementDocRef = doc(movementsRef);
      
      // FIX: Use runTransaction to read current stock atomically and prevent race conditions
      const result = await runTransaction(db, async (transaction) => {
        // 1. Read current product stock inside transaction
        const productDoc = await transaction.get(productRef);
        
        if (!productDoc.exists()) {
          throw new Error('Product not found');
        }
        
        const productData = productDoc.data();
        let currentStock;
      
      if (stockOutData.variantId) {
          // Complex product with variant - get current stock from variant
          const variant = productData.variants?.find(v => v.id === stockOutData.variantId);
          if (!variant) {
            throw new Error('Variant not found');
          }
          currentStock = variant.currentStock || 0;
        } else {
          // Simple product - get current stock directly
          currentStock = productData.currentStock || 0;
        }
        
        // 2. Validate stock availability based on transaction-read value
        if (stockOutData.quantity > currentStock) {
          throw new Error(`Cannot remove more stock than available. Available: ${currentStock}`);
        }
        
        // 3. Calculate new stock based on transaction-read value
        const newStock = currentStock - stockOutData.quantity;
        
        // 4. Update product stock
        if (stockOutData.variantId) {
          const updatedVariants = productData.variants.map(variant => 
          variant.id === stockOutData.variantId 
            ? { ...variant, currentStock: newStock }
            : variant
        );
        
          transaction.update(productRef, {
          variants: updatedVariants,
          updatedAt: serverTimestamp(),
          lastStockUpdate: serverTimestamp(),
          lastStockOperation: 'stock_out',
          lastModifiedBy: stockOutData.createdBy
        });
      } else {
          transaction.update(productRef, {
          currentStock: newStock,
          updatedAt: serverTimestamp(),
          lastStockUpdate: serverTimestamp(),
          lastStockOperation: 'stock_out',
          lastModifiedBy: stockOutData.createdBy
        });
      }

        // 5. Add stock movement record
      const movementData = {
        productId: stockOutData.productId,
        productName: stockOutData.productName,
        variantId: stockOutData.variantId || null,
        variantName: stockOutData.variantName || null,
        challanNumber: stockOutData.challanNumber,
        partyName: stockOutData.partyName,
        quantity: stockOutData.quantity,
          previousStock: currentStock, // Use transaction-read value
        newStock: newStock,
        reason: stockOutData.reason,
        notes: stockOutData.notes,
        type: 'stock_out',
        timestamp: serverTimestamp(),
        lastStockUpdate: serverTimestamp(),
        createdBy: stockOutData.createdBy,
        operation: 'remove'
      };
      
        transaction.set(movementDocRef, movementData);

      return { success: true, newStock, movementId: movementDocRef.id };
      });

      return result;
    } catch (error) {
      throw new Error(`Failed to perform stock out: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  }, [canManageInventory, organizationId, productsHook.products, validateAccess, isAuthenticated]);

  // Stock adjustment operation
  const performStockAdjustment = useCallback(async (adjustmentData) => {
    // ✅ SIMPLIFIED SECURITY: Single check for user experience
    if (!isAuthenticated) {
      throw new Error('Please log in and select an organization');
    }

    // Check if user can manage inventory (UX check - Firebase rules handle real security)
    const validation = validateAccess('CAN_MANAGE_INVENTORY');
    if (!validation.hasAccess) {
      throw new Error(validation.errorMessage);
    }

    setIsProcessing(true);
    try {
      const productRef = doc(db, `organizations/${organizationId}/products/${adjustmentData.productId}`);
      const movementsRef = collection(db, `organizations/${organizationId}/stock-movements`);
      const movementDocRef = doc(movementsRef);
      
      // FIX: Use runTransaction to read current stock atomically and prevent race conditions
      const result = await runTransaction(db, async (transaction) => {
        // 1. Read current product stock inside transaction
        const productDoc = await transaction.get(productRef);
        
        if (!productDoc.exists()) {
          throw new Error('Product not found');
        }
        
        const productData = productDoc.data();
        let currentStock;
        
        if (adjustmentData.variantId) {
          // Complex product with variant - get current stock from variant
          const variant = productData.variants?.find(v => v.id === adjustmentData.variantId);
          if (!variant) {
            throw new Error('Variant not found');
          }
          currentStock = variant.currentStock || 0;
        } else {
          // Simple product - get current stock directly
          currentStock = productData.currentStock || 0;
        }
        
        // 2. Update product stock
        if (adjustmentData.variantId) {
          const updatedVariants = productData.variants.map(variant => 
            variant.id === adjustmentData.variantId 
              ? { ...variant, currentStock: adjustmentData.newStock }
              : variant
          );
          
          transaction.update(productRef, {
            variants: updatedVariants,
            updatedAt: serverTimestamp(),
            lastStockUpdate: serverTimestamp(),
            lastStockOperation: 'adjustment',
            lastModifiedBy: adjustmentData.createdBy
          });
        } else {
          transaction.update(productRef, {
        currentStock: adjustmentData.newStock,
        updatedAt: serverTimestamp(),
        lastStockUpdate: serverTimestamp(),
        lastStockOperation: 'adjustment',
        lastModifiedBy: adjustmentData.createdBy
      });
        }

        // 3. Add stock movement record
      const movementData = {
        productId: adjustmentData.productId,
        productName: adjustmentData.productName,
        variantId: adjustmentData.variantId || null,
        variantName: adjustmentData.variantName || null,
        challanNumber: adjustmentData.challanNumber || 'ADJUSTMENT',
        partyName: adjustmentData.partyName || 'System',
          quantity: Math.abs(adjustmentData.newStock - currentStock), // Use transaction-read value
          previousStock: currentStock, // Use transaction-read value
        newStock: adjustmentData.newStock,
        reason: adjustmentData.reason,
        notes: adjustmentData.notes,
        type: 'adjustment',
        timestamp: serverTimestamp(),
        lastStockUpdate: serverTimestamp(),
        createdBy: adjustmentData.createdBy,
        operation: 'adjust'
      };
      
        transaction.set(movementDocRef, movementData);

      return { success: true, newStock: adjustmentData.newStock, movementId: movementDocRef.id };
      });

      return result;
    } catch (error) {
      throw new Error(`Failed to perform stock adjustment: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  }, [canManageInventory, organizationId, validateAccess, isAuthenticated]);

  // Get inventory statistics
  const getInventoryStats = useCallback(() => {
    const products = productsHook.products;
    const movements = stockMovementsHook.stockMovements;
    
    const stats = {
      totalProducts: products.length,
      totalStockValue: 0,
      lowStockProducts: 0,
      outOfStockProducts: 0,
      totalMovements: movements.length,
      recentMovements: movements.slice(0, 10).length,
      categories: {},
      stockAlerts: []
    };

    // Calculate product statistics
    products.forEach(product => {
      // Stock value (assuming we have a price field - for now just count items)
      stats.totalStockValue += product.currentStock || 0;
      
      // Stock alerts
      if (product.currentStock <= 0) {
        stats.outOfStockProducts++;
        stats.stockAlerts.push({
          type: 'out_of_stock',
          product: product.name,
          currentStock: product.currentStock
        });
      } else if (product.currentStock <= product.minStock) {
        stats.lowStockProducts++;
        stats.stockAlerts.push({
          type: 'low_stock',
          product: product.name,
          currentStock: product.currentStock,
          minStock: product.minStock
        });
      }

      // Category statistics
      const category = product.category || 'Uncategorized';
      if (!stats.categories[category]) {
        stats.categories[category] = {
          count: 0,
          totalStock: 0
        };
      }
      stats.categories[category].count++;
      stats.categories[category].totalStock += product.currentStock || 0;
    });

    return stats;
  }, [productsHook.products, stockMovementsHook.stockMovements]);

  // Get product history
  const getProductHistory = useCallback((productId) => {
    return stockMovementsHook.getMovementsByProduct(productId);
  }, [stockMovementsHook]);

  // Clear all errors
  const clearErrors = useCallback(() => {
    productsHook.clearError();
    stockMovementsHook.clearError();
  }, [productsHook, stockMovementsHook]);

  return {
    // Data from individual hooks
    products: productsHook.products,
    stockMovements: stockMovementsHook.stockMovements,
    loading,
    error,
    isProcessing,
    
    // Permissions
    canManageInventory,
    canViewInventory,
    
    // Combined operations
    performStockIn,
    performStockOut,
    performStockAdjustment,
    
    // Individual hook operations (for direct access)
    addProduct: productsHook.addProduct,
    updateProduct: productsHook.updateProduct,
    deleteProduct: productsHook.deleteProduct,
    updateProductStock: productsHook.updateProductStock,
    
    // Queries
    getProductById: productsHook.getProductById,
    getProductsByCategory: productsHook.getProductsByCategory,
    getLowStockProducts: productsHook.getLowStockProducts,
    getOutOfStockProducts: productsHook.getOutOfStockProducts,
    searchProducts: productsHook.searchProducts,
    getCategories: productsHook.getCategories,
    
    getMovementsByProduct: stockMovementsHook.getMovementsByProduct,
    getMovementsByType: stockMovementsHook.getMovementsByType,
    getMovementsByDateRange: stockMovementsHook.getMovementsByDateRange,
    getRecentMovements: stockMovementsHook.getRecentMovements,
    getMovementsByReason: stockMovementsHook.getMovementsByReason,
    getMovementStats: stockMovementsHook.getMovementStats,
    
    // Combined statistics
    getInventoryStats,
    getProductHistory,
    
    // Utilities
    clearErrors,
    
    // Security information
    securityLoading,
    securityError,
    isMembershipVerified
  };
}; 